import React, { Component, Suspense, createContext, lazy, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Link, Route, Routes, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronDown,
  CircleAlert,
  Code2,
  Copy,
  FileJson,
  KeyRound,
  Play,
  Search,
  Terminal,
} from 'lucide-react';
import '@fontsource-variable/inter/wght.css';
import '@fontsource-variable/newsreader/wght.css';
import '@fontsource/noto-serif-sc/chinese-simplified-500.css';
import { ArticleToc, ArticleTools, Markdown } from './components/Article.jsx';
import { CodeSample } from './components/CodeSample.jsx';
import { DocsSidebar, Header, MobileNavigation, SearchDialog } from './components/Navigation.jsx';
import { Playground } from './components/Playground.jsx';
import { useStoredApiKey } from './hooks/useStoredApiKey.js';
import { API_BASE, createRequestDraft, requestVariant } from './lib.js';
import { platformIcon } from './platform-icons.js';
import './styles.css';

const DocsContext = createContext(null);
const loadModelsPage = () => import('./features/models/ModelsPage.jsx');
const ModelsPage = lazy(() => loadModelsPage().then((module) => ({ default: module.ModelsPage })));
const METHOD_CLASS = { GET: 'get', POST: 'post', PUT: 'put', PATCH: 'patch', DELETE: 'delete' };
const jsonCache = new Map();
const PREFERRED_OPERATION = {
  v1: 'v1_chat_completions',
  'v1-models': 'v1_models',
  anthropic: 'anthropic_messages',
  'gemini-v1beta': 'gemini_v1beta_sample',
};

async function loadJson(url, signal) {
  if (jsonCache.has(url)) return jsonCache.get(url);
  const response = await fetch(url, { signal, cache: 'no-cache' });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const data = await response.json();
  jsonCache.set(url, data);
  return data;
}

function useJson(url) {
  const [state, setState] = useState(() => {
    const data = jsonCache.get(url) || null;
    return { data, error: '', loading: !data };
  });
  useEffect(() => {
    const controller = new AbortController();
    const cached = jsonCache.get(url);
    if (cached) {
      setState({ data: cached, error: '', loading: false });
      return () => controller.abort();
    }
    setState({ data: null, error: '', loading: true });
    loadJson(url, controller.signal)
      .then((data) => setState({ data, error: '', loading: false }))
      .catch((error) => { if (error.name !== 'AbortError') setState({ data: null, error: error.message, loading: false }); });
    return () => controller.abort();
  }, [url]);
  return state;
}

function useCopy() {
  const [copied, setCopied] = useState('');
  const copy = async (value, id = 'copy') => {
    await navigator.clipboard.writeText(value);
    setCopied(id);
    window.setTimeout(() => setCopied(''), 1500);
  };
  return { copied, copy };
}

function useDocumentTitle(title) {
  useEffect(() => {
    document.title = title ? `${title} · VideoCaptioner API` : '开发文档 · VideoCaptioner API';
  }, [title]);
}

function ScrollReset() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const reset = () => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      try { window.scrollTo(0, 0); } catch { /* Older mobile browsers still use the assignments above. */ }
    };
    reset();
    const frame = window.requestAnimationFrame(reset);
    return () => window.cancelAnimationFrame(frame);
  }, [pathname]);
  return null;
}

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Route render failed', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return <main className="route-error" role="alert"><CircleAlert size={24} /><h1>页面没有正确打开</h1><p>可以重新载入当前页面，已填写的 API Key 仍保存在浏览器中。</p><button onClick={() => window.location.reload()}>重新载入</button></main>;
  }
}

function MethodBadge({ method }) {
  return <span className={`method-badge ${METHOD_CLASS[method] || ''}`}>{method}</span>;
}

function LoadingPage() {
  return <div className="loading-page"><span /><span /><span /></div>;
}

function ErrorPanel({ message = '内容加载失败' }) {
  return <section className="error-panel"><CircleAlert size={20} /><div><b>{message}</b><p>请刷新页面后重试。</p></div></section>;
}

function DocsFrame({ mode, platform, operations, activeOperation, children }) {
  const { catalog, navigationOpen, setNavigationOpen, setSearchOpen } = useContext(DocsContext);
  return <div className="docs-frame">
    <DocsSidebar
      open={navigationOpen}
      onClose={() => setNavigationOpen(false)}
      onSearch={() => { setNavigationOpen(false); setSearchOpen(true); }}
      mode={mode}
      catalog={catalog}
      platform={platform}
      operations={operations}
      activeOperation={activeOperation}
    />
    <div className="docs-canvas">{children}</div>
  </div>;
}

function homeOperation(id, platform, path, authStyle = 'bearer') {
  return {
    id,
    platform,
    method: 'POST',
    path,
    auth: true,
    authStyle,
    request: { contentType: 'application/json', contentTypes: ['application/json'], variants: [{ contentType: 'application/json', fields: [] }] },
  };
}

const HOME_PROTOCOLS = [
  { id: 'chat', label: 'Chat Completions', short: 'Chat', icon: { icon: 'openai' }, href: '/reference/v1/v1_chat_completions', data: '/data/platforms/v1.json', operation: homeOperation('home-chat', 'v1', '/v1/chat/completions') },
  { id: 'responses', label: 'Responses API', short: 'Responses', icon: { icon: 'openai' }, href: '/reference/v1/v1_responses', data: '/data/platforms/v1.json', operation: homeOperation('home-responses', 'v1', '/v1/responses') },
  { id: 'claude', label: 'Claude Messages', short: 'Claude', icon: { icon: 'claude' }, href: '/reference/anthropic/anthropic_messages', data: '/data/platforms/anthropic.json', operation: homeOperation('home-claude', 'anthropic', '/v1/messages', 'anthropic') },
  { id: 'gemini', label: 'Gemini GenerateContent', short: 'Gemini', icon: { icon: 'gemini' }, href: '/reference/gemini-v1beta/gemini_v1beta_sample', data: '/data/platforms/gemini-v1beta.json', operation: homeOperation('home-gemini', 'gemini-v1beta', '/v1beta/models/gemini-3.6-flash:generateContent') },
];

function Home() {
  const { catalog, setSearchOpen } = useContext(DocsContext);
  const [protocolId, setProtocolId] = useState('chat');
  useDocumentTitle('开发文档');
  const protocol = HOME_PROTOCOLS.find((item) => item.id === protocolId) || HOME_PROTOCOLS[0];
  const quickDraft = useMemo(() => createRequestDraft(protocol.operation), [protocol]);
  const agentGuides = catalog.guides.filter((guide) => guide.section === 'Agent 工具');

  useEffect(() => {
    const controller = new AbortController();
    [...new Set(HOME_PROTOCOLS.map((item) => item.data))].forEach((url) => loadJson(url, controller.signal).catch(() => {}));
    return () => controller.abort();
  }, []);

  return <main className="home-page">
    <section className="home-hero">
      <div className="home-hero-copy">
        <p className="home-kicker">VideoCaptioner API</p>
        <h1>从这里开始构建</h1>
        <p className="home-lead">用一个 API Key 调用文本、图像、音频与视频模型。</p>
        <button className="home-search" onClick={() => setSearchOpen(true)}><Search size={19} /><span>搜索接口、平台或开发指南</span><kbd>⌘K</kbd></button>
        <div className="home-quick-links">
          <Link to="/guides/quickstart"><Play size={16} />快速开始</Link>
          <a href={catalog.site.tokenUrl}><KeyRound size={16} />获取 API Key</a>
          <Link to="/reference/v1/v1_chat_completions"><Code2 size={16} />API 参考</Link>
        </div>
      </div>
      <div className="home-protocol-shell">
        <div className="home-protocol-tabs" role="tablist" aria-label="选择 API 协议">
          {HOME_PROTOCOLS.map((item) => <button key={item.id} className={item.id === protocol.id ? 'active' : ''} role="tab" aria-selected={item.id === protocol.id} onClick={() => setProtocolId(item.id)}><img src={platformIcon(item.icon)} alt="" /><span>{item.short}</span></button>)}
        </div>
        <div className="home-code"><CodeSample operation={protocol.operation} draft={quickDraft} apiKey="" compact /></div>
        <Link className="home-protocol-link" to={protocol.href}><span>{protocol.label}</span><ArrowRight size={14} /></Link>
      </div>
    </section>

    <section className="home-agents">
      <header><p>AGENT TOOLS</p><h2>接入常用编程工具</h2><span>选择工具，复制配置。</span></header>
      <div className="agent-guide-grid">{agentGuides.map((guide) => <Link to={`/guides/${guide.slug}`} key={guide.slug}>
        <span className="agent-guide-icon"><img src={platformIcon({ icon: guide.brandIcon })} alt="" /></span>
        <span><b>{guide.title.replace(/^接入\s*/, '')}</b><small>{guide.protocol}</small></span>
        <ArrowRight size={15} />
      </Link>)}</div>
    </section>
  </main>;
}

function GuidePage() {
  const { slug = 'quickstart' } = useParams();
  const { catalog } = useContext(DocsContext);
  const { data, loading, error } = useJson(`/data/guides/${slug}.json`);
  const index = catalog.guides.findIndex((item) => item.slug === slug);
  const previous = catalog.guides[index - 1];
  const next = catalog.guides[index + 1];
  useDocumentTitle(data?.title || '开发指南');
  return <DocsFrame mode="guides"><main className="guide-page">
    {loading && <LoadingPage />}
    {error && <ErrorPanel message="指南加载失败" />}
    {data && <>
      <div className="article-toolbar"><span><BookOpen size={14} />{data.section}</span><ArticleTools source={data.markdown} /></div>
      <div className="article-layout">
        <article className="guide-article"><Markdown>{data.markdown}</Markdown>
          <nav className="article-pagination" aria-label="指南翻页">
            {previous ? <Link to={`/guides/${previous.slug}`}><small>上一篇</small><span><ArrowLeft size={15} />{previous.title}</span></Link> : <span />}
            {next ? <Link to={`/guides/${next.slug}`}><small>下一篇</small><span>{next.title}<ArrowRight size={15} /></span></Link> : <span />}
          </nav>
        </article>
        <ArticleToc markdown={data.markdown} />
      </div>
    </>}
  </main></DocsFrame>;
}

function SchemaRows({ fields = [], empty = '此接口没有可展示的字段。' }) {
  if (!fields.length) return <p className="empty-note">{empty}</p>;
  return <div className="schema-list">{fields.map((field, index) => <div className={`schema-row depth-${Math.min(Number(field.depth || 0), 3)}`} key={`${field.name}-${index}`}>
    <div className="schema-name"><code>{field.shortName || field.name}</code>{field.required && <b>必填</b>}</div>
    <div className="schema-copy"><span>{field.type || 'any'}{field.format ? ` · ${field.format}` : ''}{field.nullable ? ' · nullable' : ''}</span>{field.description && <p>{field.description}</p>}{field.enum?.length > 0 && <p>可选值：{field.enum.map(String).join('、')}</p>}{field.default !== null && field.default !== undefined && <p>默认值：<code>{String(field.default)}</code></p>}</div>
  </div>)}</div>;
}

function ParameterRows({ parameters = [] }) {
  if (!parameters.length) return null;
  return <section className="operation-section" id="request-parameters"><h2>请求参数</h2><div className="schema-list">{parameters.map((parameter) => <div className="schema-row" key={`${parameter.in}-${parameter.name}`}>
    <div className="schema-name"><code>{parameter.name}</code>{parameter.required && <b>必填</b>}</div>
    <div className="schema-copy"><span>{parameter.type || 'string'} · {parameter.in}</span>{parameter.description && <p>{parameter.description}</p>}{parameter.enum?.length > 0 && <p>可选值：{parameter.enum.map(String).join('、')}</p>}</div>
  </div>)}</div></section>;
}

function RequestBody({ operation, draft, setDraft, initialModel = '' }) {
  if (!operation.request) return null;
  const variants = operation.request.variants || [];
  const current = requestVariant(operation, draft.contentType);
  const change = (contentType) => setDraft(createRequestDraft(operation, contentType, initialModel));
  return <section className="operation-section" id="request-body">
    <div className="section-heading-row"><h2>请求体</h2>{variants.length > 1 ? <select aria-label="请求体格式" value={draft.contentType} onChange={(event) => change(event.target.value)}>{variants.map((variant) => <option key={variant.contentType}>{variant.contentType}</option>)}</select> : <span className="content-type-label">{current?.contentType || operation.request.contentType}</span>}</div>
    <SchemaRows fields={current?.fields || operation.request.fields} />
  </section>;
}

function Responses({ responses = [] }) {
  if (!responses.length) return null;
  return <section className="operation-section" id="responses"><h2>响应</h2><div className="response-list">{responses.map((response, index) => <details key={`${response.status}-${index}`} open={index === 0}>
    <summary><span className={String(response.status).startsWith('2') ? 'status-code success' : 'status-code'}>{response.status}</span><span>{response.description || '响应结果'}</span><small>{response.contentType}</small><ChevronDown size={15} /></summary>
    <div className="response-content"><SchemaRows fields={response.fields} empty="此响应没有结构化字段。" />{response.example !== null && response.example !== undefined && <details className="response-example"><summary>查看示例响应 <ChevronDown size={14} /></summary><pre><code>{JSON.stringify(response.example, null, 2)}</code></pre></details>}</div>
  </details>)}</div></section>;
}

function OperationReference({ platform, operation }) {
  const [searchParams] = useSearchParams();
  const requestedModel = searchParams.get('model') || '';
  const openRequested = searchParams.get('try') === '1';
  const [draft, setDraft] = useState(() => createRequestDraft(operation, '', requestedModel));
  const { apiKey, setApiKey } = useStoredApiKey();
  const [playgroundOpen, setPlaygroundOpen] = useState(false);
  const autoOpened = useRef('');
  const { copied, copy } = useCopy();
  useDocumentTitle(operation.summary);

  useEffect(() => setDraft(createRequestDraft(operation, '', requestedModel)), [operation, requestedModel]);
  useEffect(() => {
    const requestId = `${operation.id}:${requestedModel}`;
    if (openRequested && autoOpened.current !== requestId) {
      autoOpened.current = requestId;
      setPlaygroundOpen(true);
    }
  }, [openRequested, operation.id, requestedModel]);

  return <main className="operation-page">
    <div className="operation-grid">
      <section className="operation-intro">
        <div className="operation-breadcrumb"><img src={platformIcon(platform)} alt="" /><span>{platform.name}</span><span>/</span><span>{operation.tag}</span></div>
        <div className="operation-title-row"><h1>{operation.summary}</h1><button className="copy-page" onClick={() => copy(`${operation.method} ${operation.path}\n\n${operation.description || ''}`, 'page')}>{copied === 'page' ? <Check size={14} /> : <Copy size={14} />}{copied === 'page' ? '已复制' : '复制页面'}</button></div>
        <div className="endpoint-line"><MethodBadge method={operation.method} /><code>{operation.path}</code><button onClick={() => copy(operation.path, 'path')} aria-label="复制接口路径">{copied === 'path' ? <Check size={14} /> : <Copy size={14} />}</button></div>
        {operation.description ? <Markdown>{operation.description}</Markdown> : <p className="operation-summary">通过 {platform.name} 协议调用此接口。</p>}
        <div className="operation-actions"><button className="try-button" onClick={() => setPlaygroundOpen(true)}><Play size={15} />现场调用</button></div>
      </section>

      <aside className="operation-code-column">
        <div className="operation-code-meta"><span><Terminal size={14} />{operation.summary}</span><code>{API_BASE}</code></div>
        <CodeSample operation={operation} draft={draft} apiKey={apiKey} />
        <button className="open-workbench" onClick={() => setPlaygroundOpen(true)}><Play size={15} />使用当前参数现场调用</button>
      </aside>

      <div className="operation-details">
        {operation.auth && <section className="auth-note"><KeyRound size={17} /><div><b>{operation.authStyle === 'anthropic' ? 'x-api-key 鉴权' : 'Bearer Token 鉴权'}</b><p>{operation.authStyle === 'anthropic' ? '使用 x-api-key，并携带 anthropic-version 请求头。' : '使用 Authorization: Bearer <API Key> 请求头。'}</p></div></section>}
        <ParameterRows parameters={operation.parameters} />
        <RequestBody operation={operation} draft={draft} setDraft={setDraft} initialModel={requestedModel} />
        <Responses responses={operation.responses} />
      </div>
    </div>
    <Playground open={playgroundOpen} onClose={() => setPlaygroundOpen(false)} operation={operation} draft={draft} setDraft={setDraft} apiKey={apiKey} setApiKey={setApiKey} initialModel={requestedModel} />
  </main>;
}

function ReferencePage() {
  const { platform: platformId = 'v1', operationId } = useParams();
  const { data, loading, error } = useJson(`/data/platforms/${platformId}.json`);
  const visibleOperations = data?.operations.filter((item) => !item.deprecated) || [];
  const selectedId = operationId || PREFERRED_OPERATION[platformId];
  const operation = data?.operations.find((item) => item.id === selectedId) || visibleOperations[0] || data?.operations[0];
  return <DocsFrame mode="reference" platform={platformId} operations={loading ? null : visibleOperations} activeOperation={operation?.id}>
    {loading && <LoadingPage />}
    {error && <div className="page-state"><ErrorPanel message="API 文档加载失败" /></div>}
    {data && operation && <OperationReference key={`${platformId}-${operation.id}`} platform={data} operation={operation} />}
  </DocsFrame>;
}

function NotFound() {
  useDocumentTitle('页面不存在');
  return <main className="not-found"><FileJson size={28} /><h1>这个页面不存在</h1><p>链接可能已经更新，可以从文档首页重新进入。</p><Link to="/">返回文档首页 <ArrowRight size={15} /></Link></main>;
}

function AppReady({ catalog, searchIndex }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const location = useLocation();
  const docsMode = location.pathname.startsWith('/guides/') || location.pathname.startsWith('/reference/');
  const context = useMemo(() => ({ catalog, searchIndex, navigationOpen, setNavigationOpen, setSearchOpen }), [catalog, searchIndex, navigationOpen]);

  useEffect(() => {
    const keyboard = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') { event.preventDefault(); setSearchOpen(true); }
    };
    document.addEventListener('keydown', keyboard);
    return () => document.removeEventListener('keydown', keyboard);
  }, []);

  useEffect(() => {
    const preload = () => { loadModelsPage().catch(() => {}); };
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(preload, { timeout: 1800 });
      return () => window.cancelIdleCallback(idleId);
    }
    const timeoutId = window.setTimeout(preload, 600);
    return () => window.clearTimeout(timeoutId);
  }, []);

  useLayoutEffect(() => {
    setNavigationOpen(false);
    setSearchOpen(false);
    document.body.classList.remove('dialog-open');
  }, [location.pathname]);

  return <DocsContext.Provider value={context}>
    <ScrollReset />
    <Header onSearch={() => setSearchOpen(true)} onNavigation={() => setNavigationOpen(true)} docsMode={docsMode} />
    {!docsMode && <MobileNavigation open={navigationOpen} onClose={() => setNavigationOpen(false)} />}
    <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} searchIndex={searchIndex} catalog={catalog} />
    <RouteErrorBoundary key={location.pathname}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/guides/:slug" element={<GuidePage />} />
        <Route path="/models" element={<Suspense fallback={<LoadingPage />}><ModelsPage /></Suspense>} />
        <Route path="/reference/:platform" element={<ReferencePage />} />
        <Route path="/reference/:platform/:operationId" element={<ReferencePage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </RouteErrorBoundary>
  </DocsContext.Provider>;
}

function App() {
  const catalog = useJson('/data/catalog.json');
  const search = useJson('/data/search.json');
  if (catalog.loading || search.loading) return <LoadingPage />;
  if (catalog.error || search.error) return <ErrorPanel message="文档初始化失败" />;
  return <AppReady catalog={catalog.data} searchIndex={search.data} />;
}

createRoot(document.getElementById('root')).render(<BrowserRouter><App /></BrowserRouter>);
